import { createTrapSiteRequest, updateTrapSiteRequest } from "../../../types";
import { TrapsiteDraft } from "./types";

const sameGps = (
  a: { lat: number; lon: number },
  b: { lat: number; lon: number },
): boolean => a.lat === b.lat && a.lon === b.lon;

const sameIds = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((x) => b.includes(x));

export const buildCreatePayload = (
  draft: TrapsiteDraft,
): createTrapSiteRequest => ({
  display_name: draft.displayName.trim(),
  gps: draft.gps,
  // Omitted rather than sent empty, so the backend reverse-geocodes the pin to
  // fill both address and details instead of storing a blank address.
  ...(draft.address.trim() ? { address: draft.address.trim() } : {}),
  ...(draft.description.trim()
    ? { description: draft.description.trim() }
    : {}),
  radius_value: draft.radiusValue,
  radius_unit: draft.radiusUnit,
  // Always sent on create, even when empty: there is nothing to preserve.
  devices: draft.deviceIds,
});

/**
 * Only what actually changed.
 *
 * `devices` in particular must be omitted rather than sent unchanged - the
 * backend reads an omitted array as "leave assignments alone" and an empty one
 * as "unassign everything", and sending the list every time would make an
 * unrelated edit fight a concurrent assignment change.
 */
export const buildUpdatePayload = (
  draft: TrapsiteDraft,
  initial: TrapsiteDraft,
): updateTrapSiteRequest => {
  const payload: updateTrapSiteRequest = {};

  if (draft.displayName.trim() !== initial.displayName.trim()) {
    payload.display_name = draft.displayName.trim();
  }
  if (draft.description.trim() !== initial.description.trim()) {
    payload.description = draft.description.trim();
  }
  if (draft.address.trim() !== initial.address.trim()) {
    payload.address = draft.address.trim();
  }
  if (!sameGps(draft.gps, initial.gps)) {
    payload.gps = draft.gps;
  }
  if (
    draft.radiusValue !== initial.radiusValue ||
    draft.radiusUnit !== initial.radiusUnit
  ) {
    payload.radius_value = draft.radiusValue;
    payload.radius_unit = draft.radiusUnit;
  }
  if (!sameIds(draft.deviceIds, initial.deviceIds)) {
    payload.devices = draft.deviceIds;
  }

  return payload;
};

export const isDirty = (
  draft: TrapsiteDraft,
  initial: TrapsiteDraft,
): boolean => Object.keys(buildUpdatePayload(draft, initial)).length > 0;

export const devicesChanged = (
  draft: TrapsiteDraft,
  initial: TrapsiteDraft,
): boolean => !sameIds(draft.deviceIds, initial.deviceIds);
